// Hideo - normalizeData: defaults and migrations applied every time the profile data is read (docs/data.md).
// User files are never migrated in bulk: every list must exist (an incomplete imported file must not break rendering)
// and old formats are converted here. Existing migrations are never removed.
// Ported from the legacy data.js: the behaviour must stay identical (tests/normalize.test.ts compares it with the
// legacy output on the fixtures in tests/fixtures/normalize).
import { t } from '../i18n'
import { safeColor } from '../utils/color'
import { DEF_FORMATS, fixStatus, fixedType, formatKind, type FormatKinds } from './contentTypes'
import { TAG_NAME_MAX, hasTag, uniqTags } from './tags'
import type { ChecklistItem, ProfileData } from './types'
import { WORK_TIME_RE } from '../utils/date'
import { PROJECT_LISTS } from './items'
import { CATEGORY_NAME_MAX, isKind } from '../features/categories'
import { LOOK_ICONS, LOOK_LIB_MAX, isLookImg } from '../features/looks'
import { FORMAT_INFO_MAX, FORMAT_SONG_TITLE_MAX, FORMAT_SONG_URL_MAX } from '../features/formatSpec'
import { cleanMetrics } from '../features/metrics'

/**
 * Default checklist items, created in the UI language (they become user content: show stored labels with tv()).
 * A function, so that the labels follow the language even if this module is evaluated early.
 */
const defChecklist = (): ChecklistItem[] => [{ id: 'script', label: t('Script') }, { id: 'audio', label: t('Video ready') }, { id: 'thumbnail', label: t('Thumbnail') }]

// The input can be anything read from storage or from a file: it is checked field by field
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Loose = any

const isObject = (x: unknown): x is Record<string, Loose> => !!x && typeof x === 'object' && !Array.isArray(x)

/** Applies defaults and migrations IN PLACE and returns the same object */
export function normalizeData(d: Loose): ProfileData {
  if (!d.settings || typeof d.settings !== 'object') d.settings = {}
  if (!Array.isArray(d.settings.checklist) || !d.settings.checklist.length) d.settings.checklist = defChecklist().map(x => ({ ...x }))
  // Types are always the five fixed ones; videos with an old or custom type move to the closest one (fixedType)
  const fk: FormatKinds = isObject(d.settings.formatKinds) ? d.settings.formatKinds : {}
  if (Array.isArray(d.videos)) d.videos.forEach((v: Loose) => {
    if (v && typeof v === 'object') {
      v.format = fixedType(v.format, fk)
      if (v.status) v.status = fixStatus(formatKind(v.format), v.status)
    }
  })
  d.settings.formats = [...DEF_FORMATS]
  delete d.settings.formatKinds
  delete d.settings.typesCleaned
  for (const k of ['vault', 'videos', 'creators', 'sources', 'scripts', 'templates', 'sourceTags', 'hookTags']) if (!Array.isArray(d[k])) d[k] = []

  // Hooks (data.vault): several tags. Old data had a single "category"; the tag list also contains the used ones
  d.vault.forEach((v: Loose) => {
    if (!Array.isArray(v.tags)) v.tags = v.category ? [String(v.category)] : []
    delete v.category
    v.tags.forEach((tag: string) => { if (!hasTag(d.hookTags, tag)) d.hookTags.push(tag) })
  })
  const FM: Record<string, string> = { 'Short': 'Short Form', 'Reel': 'Short Form', 'Podcast': 'Long Form', 'Long-form': 'Long Form' }
  d.videos.forEach((v: Loose) => { v.format = FM[v.format] || v.format })
  // End of work = publication date and time: the old separate "end of work" (workEnd) becomes the publication if the
  // video had none; then it is removed. An invalid work start is cleared.
  d.videos.forEach((v: Loose) => {
    if ('workEnd' in v) {
      const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(v.workEnd || '')
      if (m && !v.publishDate) { v.publishDate = m[1]; v.publishTime = m[2] }
      delete v.workEnd
    }
    if (v.workStart && !WORK_TIME_RE.test(v.workStart)) v.workStart = ''
  })
  // Scripts: each belongs to a video (videoId). Older ones take the video that uses them (video.scriptId)
  d.scripts.forEach((s: Loose) => {
    if (typeof s.videoId !== 'string') {
      const v = d.videos.find((x: Loose) => x.scriptId === s.id)
      s.videoId = v ? v.id : ''
    }
  })
  // Editor mode: 'visual', 'md' or 'fountain' (since 11/2026); missing = visual, any other value goes (it opened as
  // visual anyway). A template keeps only mode 'fountain' (saved from a screenplay)
  d.scripts.forEach((s: Loose) => { if (s && 'mode' in s && !['visual', 'md', 'fountain'].includes(s.mode)) delete s.mode })
  d.templates.forEach((tp: Loose) => { if (tp && typeof tp === 'object' && 'mode' in tp && tp.mode !== 'fountain') delete tp.mode })
  // Creators: one or both types (stored values). Old data had a single "type"
  d.creators.forEach((c: Loose) => {
    if (!Array.isArray(c.types) || !c.types.length) c.types = [c.type === 'Collaboratore' ? 'Collaboratore' : 'Competitor']
    delete c.type
    // Banner behind the photo in the profile (added 3/11/2026): '' | "media:<id>" | http(s) address | data URL (old
    // files, moved apart by mediaExtract); anything that is not a string is ''
    if (typeof c.banner !== 'string') c.banner = ''
    // Videos still to analyse (added 3/11/2026): { id: 'ta_…', link, title }; items without an id, or with neither link
    // nor title, are dropped
    c.toAnalyse = (Array.isArray(c.toAnalyse) ? c.toAnalyse : []).filter((v: Loose) => isObject(v) && typeof v.id === 'string' && v.id &&
      ((typeof v.link === 'string' && v.link.trim()) || (typeof v.title === 'string' && v.title.trim())))
      .map((v: Loose) => ({ id: v.id, link: typeof v.link === 'string' ? v.link.trim() : '', title: typeof v.title === 'string' ? v.title.trim() : '' }))
  })
  // The profile's own categories of Sources and Saved videos (added 2/11/2026), next to the fixed ones:
  // { src: [{ id: 'ct_…', name, icon }], saved: [...] }; same-name duplicates are dropped; icon '' (the default), one of
  // LOOK_ICONS or an uploaded image (since 3/11/2026), anything else ''
  const cats = isObject(d.categories) ? d.categories : {}
  d.categories = Object.fromEntries((['src', 'saved'] as const).map(g => {
    const seen = new Set<string>()
    return [g, (Array.isArray(cats[g]) ? cats[g] : []).filter((c: Loose) => {
      if (!isObject(c) || typeof c.id !== 'string' || !/^ct_/.test(c.id) || typeof c.name !== 'string' || !c.name.trim()) return false
      const k = c.name.trim().toLowerCase()
      if (seen.has(k)) return false
      seen.add(k)
      return true
    }).map((c: Loose) => ({ id: c.id, name: c.name.trim().slice(0, CATEGORY_NAME_MAX), icon: LOOK_ICONS.includes(c.icon) || isLookImg(c.icon) ? c.icon : '' }))]
  }))
  // Sources: several tags. Old data had a single "category" (it became a tag: never remove this; the categories added
  // on 2/11/2026 are stored as "kind"); the tag list also contains the used ones
  d.sources.forEach((s: Loose) => {
    if (!Array.isArray(s.tags)) s.tags = s.category ? [String(s.category)] : []
    delete s.category
    if (!isKind(d, 'src', s.kind)) s.kind = ''
    s.tags.forEach((tag: string) => { if (!hasTag(d.sourceTags, tag)) d.sourceTags.push(tag) })
  })
  // Formats (stored as "series"): { id, name, color, projectId, icon }; a video has at most one (seriesId).
  // icon: '' | Font Awesome class | data URL image; the real check happens when drawing (cleanLook)
  if (!Array.isArray(d.series)) d.series = []
  d.series = d.series.filter((s: Loose) => s && s.id && s.name).map((s: Loose) => {
    // The format's card (Formats page, added 2/11/2026): description, content type, and the other fields as texts by
    // field id (src/features/formatSpec.ts; values of fields no longer shown are kept)
    const info: Record<string, string> = isObject(s.info) ? Object.fromEntries(Object.entries(s.info).filter(([k, v]) => /^[a-zA-Z][a-zA-Z0-9]*$/.test(k) && typeof v === 'string')
      .map(([k, v]) => [k, (v as string).slice(0, FORMAT_INFO_MAX)])) : {}
    // Reference songs (3/11/2026): missing → the old "Audio or trend" text of the card as the first song (the text stays
    // in info.audio); done once, a removed song never comes back
    const audio = String(info.audio || '').trim()
    const songs = Array.isArray(s.songs)
      ? s.songs.filter((x: Loose) => x && typeof x.title === 'string' && x.title.trim()).map((x: Loose, k: number) => ({
        id: typeof x.id === 'string' && x.id ? x.id : 'so_' + k, title: x.title.slice(0, FORMAT_SONG_TITLE_MAX), url: typeof x.url === 'string' ? x.url.slice(0, FORMAT_SONG_URL_MAX) : '' }))
      : audio ? [{ id: 'so_audio', title: audio.slice(0, FORMAT_SONG_TITLE_MAX), url: '' }] : []
    // ...s first: fields this version does not know (added by a later one) are kept, not dropped on save
    return {
      ...s,
      id: String(s.id), name: String(s.name), color: safeColor(s.color), projectId: s.projectId || '',
      icon: typeof s.icon === 'string' && (/^fa-[a-z0-9-]+$/.test(s.icon) || /^data:image\/(png|jpeg|webp);base64,/.test(s.icon)) ? s.icon : '',
      desc: typeof s.desc === 'string' ? s.desc : '',
      type: DEF_FORMATS.includes(s.type) ? s.type : '',
      // Several content types (3/11/2026): missing → the old single type, if any
      types: Array.isArray(s.types) ? DEF_FORMATS.filter(f => s.types.includes(f)) : DEF_FORMATS.includes(s.type) ? [s.type] : [],
      info, songs
    }
  })
  // Projects: every video, format, hook, creator, source and keyword has its projectId; an item without a valid project
  // (old files, imports) goes into the first project. With no projects the user is asked to create one
  if (!Array.isArray(d.projects)) d.projects = []
  d.projects = d.projects.filter((p: Loose) => p && p.id && p.name).map((p: Loose) => ({
    id: String(p.id), name: String(p.name), color: safeColor(p.color), descFooter: typeof p.descFooter === 'string' ? p.descFooter : ''
  }))
  // Saved videos: { id, title, url, projectId, note, thumbnail, savedAt, tags }; without a link they are of no use
  if (!Array.isArray(d.savedVideos)) d.savedVideos = []
  d.savedVideos = d.savedVideos.filter((v: Loose) => v && typeof v === 'object' && v.id && typeof v.url === 'string' && v.url.trim())
  d.savedVideos.forEach((v: Loose) => {
    if (typeof v.title !== 'string') v.title = ''
    if (typeof v.savedAt !== 'number') v.savedAt = Number(String(v.id).replace(/\D/g, '')) || 0
    if (typeof v.seriesId !== 'string') v.seriesId = ''
    // Saved videos have their own tags, separate from the plan's formats: the format they had (seriesId, kept for older
    // versions) becomes a tag with the same name, icon and colour, once (when tags is missing)
    if (!Array.isArray(v.tags)) {
      const s = d.series.find((x: Loose) => x.id === v.seriesId)
      v.tags = s ? [s.name.trim().slice(0, TAG_NAME_MAX)] : []
      if (s) {
        if (!isObject(d.tagStyle)) d.tagStyle = {}
        const P = isObject(d.tagStyle[v.projectId]) ? d.tagStyle[v.projectId] : (d.tagStyle[v.projectId] = {})
        const M = isObject(P.saved) ? P.saved : (P.saved = {}), key = v.tags[0].toLowerCase()
        if (!M[key]) M[key] = s.icon ? { color: s.color, icon: s.icon } : { color: s.color }
      }
    }
    v.tags = uniqTags(v.tags.filter((x: Loose) => typeof x === 'string' && x.trim()))
    if (!isKind(d, 'saved', v.kind)) v.kind = ''
  })
  // Keyword bank: { id, text, status: 'da usare' | 'usata' (stored values), projectId }
  if (!Array.isArray(d.keywords)) d.keywords = []

  // Repair: projects with the same name (created twice by a double click) become one; the duplicate's content, formats and
  // plans move to the first. Formats with the same name in the same project are merged
  const keep: Record<string, Loose> = {}, merged: Record<string, string> = {}
  d.projects = d.projects.filter((p: Loose) => {
    const k = p.name.trim().toLowerCase()
    if (keep[k]) { merged[p.id] = keep[k].id; return false }
    keep[k] = p
    return true
  })
  if (Object.keys(merged).length) {
    PROJECT_LISTS.forEach(k => d[k].forEach((x: Loose) => { if (merged[x.projectId]) x.projectId = merged[x.projectId] }))
    if (Array.isArray(d.settings.pedViews)) d.settings.pedViews.forEach((v: Loose) => {
      if (Array.isArray(v.projects)) v.projects = [...new Set(v.projects.map((id: string) => merged[id] || id))]
    })
    const sk: Record<string, Loose> = {}, smerged: Record<string, string> = {}
    d.series = d.series.filter((s: Loose) => {
      const k = s.projectId + '|' + s.name.trim().toLowerCase()
      if (sk[k]) { smerged[s.id] = sk[k].id; return false }
      sk[k] = s
      return true
    })
    d.videos.forEach((v: Loose) => { if (smerged[v.seriesId]) v.seriesId = smerged[v.seriesId] })
    d.savedVideos.forEach((v: Loose) => { if (smerged[v.seriesId]) v.seriesId = smerged[v.seriesId] })
    // the duplicate's tags move to the remaining project too
    if (d.projectTags && typeof d.projectTags === 'object') Object.keys(merged).forEach(dup => {
      const from = d.projectTags[dup]
      if (!from) return
      const to = d.projectTags[merged[dup]] || (d.projectTags[merged[dup]] = { hook: [], src: [], saved: [] })
      ;(['hook', 'src', 'saved'] as const).forEach(k => (from[k] || []).forEach((tag: string) => {
        if (!Array.isArray(to[k])) to[k] = []
        if (!hasTag(to[k], tag)) to[k].push(tag)
      }))
      delete d.projectTags[dup]
    })
    // and so does their tags' look (icons and colours), without overwriting the remaining project's
    if (d.tagStyle && typeof d.tagStyle === 'object') Object.keys(merged).forEach(dup => {
      const from = d.tagStyle[dup]
      if (!from || typeof from !== 'object') return
      const to = d.tagStyle[merged[dup]] || (d.tagStyle[merged[dup]] = {})
      ;(['hook', 'src', 'saved'] as const).forEach(k => Object.entries(from[k] || {}).forEach(([tag, v]) => {
        to[k] = to[k] || {}
        if (!to[k][tag]) to[k][tag] = v
      }))
      delete d.tagStyle[dup]
    })
  }
  if (d.projects.length) {
    const ids = new Set<string>(d.projects.map((p: Loose) => p.id)), first: string = d.projects[0].id
    PROJECT_LISTS.forEach(k => d[k].forEach((x: Loose) => { if (!ids.has(x.projectId)) x.projectId = first }))
    // Per-project tags: { <project id>: { hook: [...], src: [...], saved: [...] } }. The first time they come from the profile's old
    // lists: each tag goes into the projects that use it (a tag used in two projects becomes two tags, one per project);
    // an unused tag goes into the first project. After that, every tag used by an item always exists in its project.
    const seed = !d.projectTags || typeof d.projectTags !== 'object' || Array.isArray(d.projectTags)
    if (seed) d.projectTags = {}
    d.projects.forEach((p: Loose) => {
      const e = d.projectTags[p.id] = d.projectTags[p.id] || {}
      ;(['hook', 'src', 'saved'] as const).forEach(k => { if (!Array.isArray(e[k])) e[k] = [] })
    })
    const TK = [['hook', 'vault', 'hookTags'], ['src', 'sources', 'sourceTags'], ['saved', 'savedVideos', '']] as const
    if (seed) TK.forEach(([k, items, list]) => list && d[list].forEach((tag: string) => {
      const users = [...new Set(d[items].filter((x: Loose) => hasTag(x.tags, tag)).map((x: Loose) => x.projectId))] as string[]
      ;(users.length ? users : [first]).forEach(pid => {
        const L = d.projectTags[pid][k]
        if (!hasTag(L, tag)) L.push(tag)
      })
    }))
    TK.forEach(([k, items]) => d[items].forEach((x: Loose) => x.tags.forEach((tag: string) => {
      const L = d.projectTags[x.projectId][k]
      if (hasTag(L, tag)) return
      L.push(tag)
      // A tag that arrived with a moved or copied item brings the icon and colour it had in the other project
      const S = d.tagStyle, key = tag.toLowerCase()
      if (S && typeof S === 'object' && !Array.isArray(S)) {
        const src = Object.values(S).find((p: Loose) => p && p[k] && p[k][key]) as Loose
        if (src) {
          const to = S[x.projectId] || (S[x.projectId] = {})
          to[k] = to[k] || {}
          if (!to[k][key]) to[k][key] = { ...src[k][key] }
        }
      }
    })))
    Object.keys(d.projectTags).forEach(id => { if (!ids.has(id)) delete d.projectTags[id] })
    // The profile lists stay as the union (compatibility with older files and versions)
    d.hookTags = uniqTags(d.projects.flatMap((p: Loose) => d.projectTags[p.id].hook))
    d.sourceTags = uniqTags(d.projects.flatMap((p: Loose) => d.projectTags[p.id].src))
    // Tag look: { <project id>: { hook: { <lower-case name>: { color, icon } }, src: {...}, saved: {...} } }
    if (!isObject(d.tagStyle)) d.tagStyle = {}
    Object.keys(d.tagStyle).forEach(id => { if (!ids.has(id) || !d.tagStyle[id] || typeof d.tagStyle[id] !== 'object') delete d.tagStyle[id] })
  }
  // Saved plans: { id, name, projects: [project id] }, no limit, existing projects only
  const pids = new Set<string>(d.projects.map((p: Loose) => p.id))
  d.settings.pedViews = (Array.isArray(d.settings.pedViews) ? d.settings.pedViews : [])
    .filter((v: Loose) => v && v.id && v.name)
    .map((v: Loose) => ({ id: String(v.id), name: String(v.name), projects: (Array.isArray(v.projects) ? v.projects : []).filter((id: string) => pids.has(id)) }))
    .filter((v: Loose) => v.projects.length)
  // Library of uploaded icon images (added 2/11/2026): valid image data URLs, no doubles, the newest LOOK_LIB_MAX
  d.lookImages = [...new Set<string>((Array.isArray(d.lookImages) ? d.lookImages : []).filter(isLookImg))].slice(0, LOOK_LIB_MAX)
  // Sources and saved videos linked to a video (its references, added 2/11/2026): ids of existing items, no doubles
  const srcIds = new Set<string>(d.sources.map((x: Loose) => x.id)), savedIds = new Set<string>(d.savedVideos.map((x: Loose) => x.id))
  d.videos.forEach((v: Loose) => {
    v.sourceIds = [...new Set<string>((Array.isArray(v.sourceIds) ? v.sourceIds : []).filter((id: Loose) => srcIds.has(id)))]
    v.savedIds = [...new Set<string>((Array.isArray(v.savedIds) ? v.savedIds : []).filter((id: Loose) => savedIds.has(id)))]
    // How it is doing on each platform (added 2/11/2026): known platforms, valid verdicts and numbers, empty ones dropped
    v.metrics = cleanMetrics(v.metrics)
  })
  // Online images and voice notes: { <media id>: <Drive file id> (old saves) | { google, dropbox } }
  if (!isObject(d.media)) d.media = {}
  // Information that only lives in saved files, not in the in-memory data
  delete d.savedAt
  delete d.savedOn
  delete d.profile
  return d as ProfileData
}
